/* =============================================================
   Motive Product Page — UX Enhancements
   motive-products.js v3.31
   Load in theme.liquid WITHOUT defer, inside <head>:
   <script src="{{ 'motive-products.js' | asset_url }}"></script>

   The gallery is ordered on the server (snippets/motive-gallery-order,
   v3.20): every product page arrives with the selected colour's media
   only, in Motive order. This module never moves a slide.

   v3.27 (Oct 2 2026): the v3.2–v3.9 client sorter is removed. It only
   ran on pages without data-motive-server-gallery, and all 411 active
   products render product.quote.json with the server gallery on. Gone
   with it: the first-paint hide, the per-page product .js fetch, the
   variant image preloads and the body-level rebuild observer. The
   v3.9 gallery law now lives in snippets/motive-gallery-order.

   v3.30 (Oct 8 2026): clean address bar. Shopify's search and
   recommendation tracking parameters are stripped once the page has
   loaded, so the bar always reads product + colour and copies clean.

   v3.31 (Oct 28 2026): every option value stays on the page. Values with
   no variant in the current combination are crossed out, not hidden
   (Taylor: "keep all the capacities present"), and clicking one lands on
   a real variant.

   What remains:
   0. Clean address bar (v3.30).
   1. Independent right-panel scroll (desktop).
   2. Scroll back to the gallery on a variant change.
   3. Server gallery colour swap (v3.20/v3.21).
   4. Crossed-out options land on a real variant (v3.31).
   ============================================================= */

(function () {
  'use strict';

  if (!window.location.pathname.includes('/products/')) return;

  /* Version constant — survives minification so deploys can be
     verified against the CDN with a string check. */
  var MOTIVE_PRODUCTS_VERSION = 'motive-products-v3.31';
  window.MOTIVE_PRODUCTS_VERSION = MOTIVE_PRODUCTS_VERSION;


  // ── 0. CLEAN ADDRESS BAR ───────────────────────────────────────────────
  // Links from search and recommendations carry tracking parameters
  // (?_pos=1&_sid=…&_ss=r). Strip them after load (analytics has read the
  // visit by then), keeping ?variant. The theme rewrites the bar from the
  // current location on every colour change, so one clean-up keeps it clean.

  var TRACKING_PARAMS = ['_pos', '_sid', '_ss', '_psq', '_fid', '_v',
    'pr_prod_strat', 'pr_rec_id', 'pr_rec_pid', 'pr_ref_pid', 'pr_seq'];

  function cleanAddressBar() {
    try {
      var url = new URL(window.location.href);
      var changed = false;
      TRACKING_PARAMS.forEach(function (key) {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (changed) {
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
    } catch (e) {}
  }

  if (document.readyState === 'complete') {
    cleanAddressBar();
  } else {
    window.addEventListener('load', cleanAddressBar);
  }


  // ── 1. INDEPENDENT RIGHT PANEL SCROLL ──────────────────────────────────

  function fixScrollLock() {
    var style = document.createElement('style');
    style.id = 'motive-scroll-fix';
    style.textContent = [
      '@media screen and (min-width: 1024px) {',
      '  .product-content-container {',
      '    position: sticky !important;',
      '    top: 62px !important;',
      '    align-self: start !important;',
      '    height: calc(100vh - 62px) !important;',
      '  }',
      '  .product-content-container .product-block-area {',
      '    height: 100% !important;',
      '    max-height: calc(100vh - 62px) !important;',
      '    overflow-y: auto !important;',
      '    overflow-x: hidden !important;',
      '    -webkit-overflow-scrolling: touch;',
      '    scrollbar-width: none;',
      '  }',
      '  .product-content-container .product-block-area::-webkit-scrollbar {',
      '    display: none;',
      '  }',
      '  .product-media-container {',
      '    align-self: start !important;',
      '  }',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  }


  // ── 2. SCROLL PAGE TO TOP OF MEDIA ON VARIANT CHANGE ───────────────────

  function scrollToMediaTop() {
    var mediaContainer = document.querySelector('.product-media-container');
    if (!mediaContainer) return;
    var rect = mediaContainer.getBoundingClientRect();
    if (rect.top > -50 && rect.top < window.innerHeight) return;
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    window.scrollTo({ top: scrollTop + rect.top - 104, behavior: 'smooth' });
  }

  function watchVariantChanges() {
    var currentVariantDiv = document.querySelector('[data-variant-fragment="current-variant"]');
    if (currentVariantDiv) {
      new MutationObserver(function () {
        setTimeout(scrollToMediaTop, 150);
      }).observe(currentVariantDiv, {
        childList: true, subtree: true,
        characterData: true, attributes: true
      });
    }

    document.addEventListener('click', function (e) {
      var btn = e.target.closest(
        '[data-variant-picker] label, [data-variant-picker] button, ' +
        '[data-product-option] label, [data-product-option] button'
      );
      if (btn) setTimeout(scrollToMediaTop, 200);
    });

    document.addEventListener('variant:change', function () {
      setTimeout(scrollToMediaTop, 150);
    });

    document.addEventListener('change', function (e) {
      if (e.target.closest('[data-variant-picker]') && e.target.type === 'radio') {
        setTimeout(scrollToMediaTop, 150);
      }
    });
  }


  // ── 3. SERVER GALLERY COLOUR SWAP (v3.20) ──────────────────────────────
  /* The theme only morphs [data-variant-fragment]s on a variant change,
     so the server-rendered gallery would keep the old colour. When the
     new variant's featured media is not already rendered (= a different
     colour), fetch the section for that variant and swap it whole — the
     same path the theme uses for product-group swaps. Size changes and
     the new island's own init event find their media rendered: no-op. */
  var swapToken = 0;

  function watchServerColourSwap() {
    document.addEventListener('theme:variant:change', function (e) {
      var d = e.detail || {};
      var root = d.productRootEl;
      if (!root || !root.hasAttribute('data-motive-server-gallery')) return;
      var fm = d.variant && d.variant.featured_media;
      if (!fm) return;
      var rendered = (root.getAttribute('data-motive-gallery-media') || '').split(',');
      if (rendered.indexOf(String(fm.id)) !== -1) return;

      var section = root.closest('.shopify-section');
      if (!section) return;
      var sectionId = section.id.replace('shopify-section-', '');
      var token = ++swapToken;
      fetch(window.location.pathname + '?variant=' + d.variant.id + '&section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (token !== swapToken) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.getElementById(section.id);
          var live = document.getElementById(section.id);
          if (fresh && live) {
            live.replaceWith(fresh);
            /* v3.21 — nodes parsed by DOMParser never execute their
               inline <script>s. Re-create them so the section's own
               scripts (accordions, measurements, Pantone row) run
               exactly as on a page load. External scripts are skipped. */
            fresh.querySelectorAll('script:not([src])').forEach(function (old) {
              var t = (old.getAttribute('type') || '').toLowerCase();
              if (t && t !== 'text/javascript' && t !== 'module') return;
              var s = document.createElement('script');
              if (t) s.type = t;
              s.textContent = old.textContent;
              old.replaceWith(s);
            });
          }
        })
        .catch(function () {});
    });
  }


  // ── 4. CROSSED-OUT OPTIONS LAND ON A REAL VARIANT (v3.31) ──────────────
  /* product.quote.json no longer hides option values that have no variant
     in the current combination; they render crossed out (.unavailable).
     Shopify answers an impossible combination with no variant at all, so
     before the theme's own @change handler reads the selection, re-check
     the other options to the closest real variant: the clicked value
     stays, every other current choice that still exists with it stays
     (earlier options first), else the first rendered value that does.
     Hidden colours are never rendered, so they are never landed on.
     The theme then fetches a real combination through its normal path. */
  function watchUnavailableOptions() {
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || !el.matches || !el.matches('[data-single-option-selector].unavailable')) return;
      var picker = el.closest('[data-variant-picker]');
      var mapEl = picker && picker.querySelector('[data-motive-variant-map]');
      if (!mapEl) return;
      var variants;
      try { variants = JSON.parse(mapEl.textContent); } catch (_e) { return; }
      var pos = parseInt(el.getAttribute('data-position'), 10) - 1;
      var rendered = [], current = [];
      picker.querySelectorAll('[data-single-option-selector]').forEach(function (i) {
        var p = parseInt(i.getAttribute('data-position'), 10) - 1;
        (rendered[p] = rendered[p] || {})[i.value] = i;
        if (i.checked && i !== el) current[p] = i.value;
      });
      var best = null, bestScore = -1;
      variants.forEach(function (v) {
        var o = v.o, p, score;
        if (o[pos] !== el.value) return;
        for (p = 0; p < o.length; p++) if (!rendered[p] || !rendered[p][o[p]]) return;
        score = v.a ? 1 : 0;
        for (p = 0; p < o.length; p++) {
          if (p !== pos && o[p] === current[p]) score += Math.pow(2, o.length - p);
        }
        if (score > bestScore) { best = o; bestScore = score; }
      });
      if (!best) return;
      for (var p = 0; p < best.length; p++) {
        if (p !== pos && !rendered[p][best[p]].checked) rendered[p][best[p]].checked = true;
      }
    }, true);
  }


  // ── INIT ────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    if (!document.querySelector('[data-product-root]')) return;
    fixScrollLock();
    watchServerColourSwap();
    watchVariantChanges();
    watchUnavailableOptions();
  });

})();

/* =============================================================
   Motive Collection View Toggle — Studio / Scene
   motive-products.js — collection module

   Inverts the default-vs-hover image relationship at the grid level.
     Studio (default): variant featured_image (flat lay) shows;
                       hover reveals the second media (model/scene shot).
     Scene:            second media shows by default;
                       hover reveals the flat lay.

   - Toggles a class on every .collection-list element.
   - Persists choice in localStorage so it survives navigation.
   - Re-applies after Shopify Section Rendering API filter/sort updates
     via MutationObserver on #results.
   - Click handling uses event delegation on document so it works after
     DOM swaps without re-binding.
   ============================================================= */

(function () {
  'use strict';

  /* v3.28 (Oct 2 2026): Wear Model | Flat toggle — replaces the retired
     Product | In Use toggle. Flat adds .motive-view-flat to <body>; CSS
     raises each model-led wear tile's hidden flat-lay layer
     (.motive-tile-flat, rendered with data-mp-src so it only downloads
     once Flat is chosen). Session-scoped: a new visit starts on Model.
     Hidden on the Headwear tab — no models there. */

  if (!/^\/collections\//.test(window.location.pathname)) return;

  var STORAGE_KEY = 'motive-wear-view';
  var FLAT_CLASS = 'motive-view-flat';
  var PARAM = 'filter.p.m.custom.subcategory';

  function getStored() {
    try { return sessionStorage.getItem(STORAGE_KEY) === 'flat' ? 'flat' : 'model'; }
    catch (e) { return 'model'; }
  }

  function setStored(v) {
    try { sessionStorage.setItem(STORAGE_KEY, v); } catch (e) {}
  }

  function onHeadwear() {
    var vals = new URLSearchParams(window.location.search).getAll(PARAM);
    return vals.length === 1 && vals[0].toLowerCase() === 'headwear';
  }

  function hydrateFlats() {
    var imgs = document.querySelectorAll('.motive-tile-flat img[data-mp-src]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.dataset.mpSrcset) { img.srcset = img.dataset.mpSrcset; img.removeAttribute('data-mp-srcset'); }
      img.src = img.dataset.mpSrc; img.removeAttribute('data-mp-src');
    }
  }

  function apply() {
    var toggle = document.querySelector('[data-motive-view-toggle]');
    if (!toggle) return;
    var hidden = onHeadwear();
    toggle.classList.toggle('is-hidden', hidden);
    var v = hidden ? 'model' : getStored();
    document.body.classList.toggle(FLAT_CLASS, v === 'flat');
    if (v === 'flat') hydrateFlats();
    var btns = toggle.querySelectorAll('[data-view]');
    for (var j = 0; j < btns.length; j++) {
      btns[j].setAttribute('aria-pressed', btns[j].getAttribute('data-view') === v ? 'true' : 'false');
    }
  }

  function init() {
    if (!document.querySelector('[data-motive-view-toggle]')) return;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-motive-view-toggle] [data-view]');
      if (!btn) return;
      var v = btn.getAttribute('data-view');
      if (v !== 'model' && v !== 'flat') return;
      setStored(v);
      apply();
    });
    var results = document.getElementById('results');
    if (results) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; apply(); });
      }).observe(results, { childList: true, subtree: true });
    }
    window.addEventListener('popstate', apply);
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* =============================================================
   Motive Collection Specs Toggle — show/hide manufacturing details
   motive-products.js — collection module

   Adds a `motive-specs-on` class to every .collection-list when
   the buyer toggles Specs. CSS reveals .motive-tile-specs rows
   inside each tile.

   Mirrors the View toggle architecture: localStorage persistence,
   click delegation, MutationObserver on #results so state survives
   filter/sort updates from Shopify's Section Rendering API.
   ============================================================= */

(function () {
  'use strict';

  var path = window.location.pathname;
  if (!/^\/collections\//.test(path) && !/^\/search/.test(path)) return;

  var STORAGE_KEY = 'motive-collection-specs';
  var SPECS_CLASS = 'motive-specs-on';

  function getStored() {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'on';
    } catch (e) {
      return false;
    }
  }

  function setStored(on) {
    try { localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off'); } catch (e) {}
  }

  function applyState(on) {
    var lists = document.querySelectorAll('.collection-list');
    for (var i = 0; i < lists.length; i++) {
      lists[i].classList.toggle(SPECS_CLASS, on);
    }
    var btns = document.querySelectorAll('[data-motive-specs-toggle]');
    for (var j = 0; j < btns.length; j++) {
      btns[j].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  function bindClicks() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-motive-specs-toggle]');
      if (!btn) return;
      var current = btn.getAttribute('aria-pressed') === 'true';
      var next = !current;
      setStored(next);
      applyState(next);
    });
  }

  function startObserver() {
    var results = document.getElementById('results');
    if (!results) return;
    var observer = new MutationObserver(function () {
      applyState(getStored());
    });
    observer.observe(results, { childList: true, subtree: true });
  }

  function init() {
    applyState(getStored());
    bindClicks();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* =============================================================
   Motive Collection Density + Sticky Toolbar (v3.22, Sept 29 2026)
   motive-products.js — collection module

   Density: Large | Small toggle. Large adds `.motive-density-large`
   to every .collection-list (CSS: 3 desktop / 1 mobile) and widens
   tile image `sizes` so the browser fetches sharp enough files.
   A filler cell squares off the last desktop row (the theme's own
   placeholders are sized for its 4-up grid). Same architecture as
   the Details toggle: localStorage, click delegation, MutationObserver
   on #results so state survives filter/sort re-renders.

   Sticky toolbar: [data-motive-toolbar] is position:sticky in CSS and
   always pinned (v3.23 dropped the tuck-on-scroll behaviour).
   ============================================================= */

(function () {
  'use strict';

  var path = window.location.pathname;
  if (!/^\/collections\//.test(path) && !/^\/search/.test(path)) return;

  var STORAGE_KEY = 'motive-collection-density';
  var LARGE_CLASS = 'motive-density-large';
  var LARGE_SIZES = '(min-width: 1024px) 34vw, 100vw';
  var desktopMQ = window.matchMedia('(min-width: 1024px)');

  function getStored() {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'large' ? 'large' : 'small';
    } catch (e) {
      return 'small';
    }
  }

  function setStored(v) {
    try { localStorage.setItem(STORAGE_KEY, v); } catch (e) {}
  }

  function syncSizes(list, large) {
    var imgs = list.querySelectorAll('img[sizes]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (large) {
        if (!img.hasAttribute('data-motive-sizes')) {
          img.setAttribute('data-motive-sizes', img.getAttribute('sizes'));
        }
        if (img.getAttribute('sizes') !== LARGE_SIZES) img.setAttribute('sizes', LARGE_SIZES);
      } else if (img.hasAttribute('data-motive-sizes')) {
        img.setAttribute('sizes', img.getAttribute('data-motive-sizes'));
        img.removeAttribute('data-motive-sizes');
      }
    }
  }

  function syncFiller(list, large) {
    var old = list.querySelector(':scope > li.motive-density-filler');
    if (!large || !desktopMQ.matches) {
      if (old) old.remove();
      return;
    }
    var count = 0;
    var items = list.children;
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      if (li.tagName !== 'LI' || li.getAttribute('role') === 'presentation' ||
          li.classList.contains('col-span-full')) continue;
      count++;
    }
    var span = (3 - (count % 3)) % 3;
    if (!span) {
      if (old) old.remove();
      return;
    }
    var filler = old || document.createElement('li');
    if (!old) {
      filler.className = 'motive-density-filler bg-scheme-background';
      filler.setAttribute('aria-hidden', 'true');
      filler.setAttribute('role', 'presentation');
    }
    filler.style.setProperty('--motive-filler-span', span);
    if (filler !== list.lastElementChild) list.appendChild(filler);
  }

  function applyState(v) {
    var large = v === 'large';
    var lists = document.querySelectorAll('.collection-list');
    for (var i = 0; i < lists.length; i++) {
      lists[i].classList.toggle(LARGE_CLASS, large);
      syncSizes(lists[i], large);
      syncFiller(lists[i], large);
    }
    var btns = document.querySelectorAll('[data-motive-density-toggle] [data-density]');
    for (var j = 0; j < btns.length; j++) {
      btns[j].setAttribute('aria-pressed', btns[j].getAttribute('data-density') === v ? 'true' : 'false');
    }
  }

  function bindClicks() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-motive-density-toggle] [data-density]');
      if (!btn) return;
      var v = btn.getAttribute('data-density');
      if (v !== 'large' && v !== 'small') return;
      setStored(v);
      applyState(v);
    });
  }

  function startObserver() {
    var results = document.getElementById('results');
    if (!results) return;
    var pending = false;
    new MutationObserver(function () {
      /* Coalesce to one pass per frame; our own filler/sizes writes
         are idempotent, so the echo mutation settles immediately. */
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        applyState(getStored());
      });
    }).observe(results, { childList: true, subtree: true });
  }

  function init() {
    applyState(getStored());
    bindClicks();
    startObserver();
    var onMQ = function () { applyState(getStored()); };
    if (desktopMQ.addEventListener) desktopMQ.addEventListener('change', onMQ);
    else if (desktopMQ.addListener) desktopMQ.addListener(onMQ);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();



/* =============================================================
   Motive Subnav Sync (v3.24, Sept 29 2026)
   The subcategory bar (snippets/sub-collection-nav) is rendered
   outside #results, so in-place filtering from the drawer never
   refreshes it. After each results update (and on back/forward),
   move the 3px underline to the link matching the URL's
   subcategory value — "All" when none is set. v3.25: same for the
   product count in the collection title.
   ============================================================= */

(function () {
  'use strict';

  if (!/^\/collections\//.test(window.location.pathname)) return;

  var PARAM = 'filter.p.m.custom.subcategory';

  function valueOf(href) {
    try { return new URL(href, window.location.origin).searchParams.get(PARAM) || ''; }
    catch (e) { return ''; }
  }

  function sync() {
    var nav = document.querySelector('[data-motive-subnav]');
    if (!nav) return;
    var current = new URLSearchParams(window.location.search).get(PARAM) || '';
    var links = nav.querySelectorAll('a');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var isAll = a.textContent.trim() === 'All';
      var active = isAll ? current === '' : valueOf(a.href) === current;
      var span = a.querySelector('span');
      if (active && !span) {
        var s = document.createElement('span');
        s.style.borderBottom = '3px solid currentColor';
        s.style.paddingBottom = '2px';
        s.textContent = a.textContent.trim();
        a.textContent = '';
        a.appendChild(s);
        a.setAttribute('aria-current', 'page');
      } else if (!active && span) {
        a.textContent = span.textContent;
        a.removeAttribute('aria-current');
      }
    }
  }

  /* v3.25 — the title count (<sup data-motive-title-count>) also lives
     outside #results. Each results render carries the filtered total in
     [data-motive-count]; copy it into the title's first text node. */
  function syncCount() {
    var src = document.querySelector('#results [data-motive-count]');
    var sup = document.querySelector('[data-motive-title-count]');
    if (!src || !sup) return;
    var n = src.getAttribute('data-motive-count');
    for (var i = 0; i < sup.childNodes.length; i++) {
      var node = sup.childNodes[i];
      if (node.nodeType === 3 && node.textContent.trim() !== '') {
        if (node.textContent.trim() !== n) node.textContent = n;
        return;
      }
    }
  }

  function init() {
    var results = document.getElementById('results');
    if (results) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; sync(); syncCount(); });
      }).observe(results, { childList: true });
    }
    window.addEventListener('popstate', function () { setTimeout(sync, 50); });
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* =============================================================
   Motive Load More (v3.29, Oct 2 2026)
   Collections only. "Load more" fetches the next page through the
   Section Rendering API and appends its tiles to the same grid, then
   swaps in that page's footer (count, bar, next link — or none on the
   last page). The button is a real next-page href, so a failed fetch
   or no-JS simply pages. Filter/sort re-renders replace #results and
   start over at page 1. Back button: pages loaded + scroll are kept
   per URL in sessionStorage and rebuilt on back/forward.
   ============================================================= */

(function () {
  'use strict';

  if (!/^\/collections\//.test(window.location.pathname)) return;

  var busy = false;

  function key() { return 'motive-loadmore:' + location.pathname + location.search; }
  function readState() {
    try { return JSON.parse(sessionStorage.getItem(key()) || 'null'); } catch (e) { return null; }
  }
  function writeState(s) {
    try { sessionStorage.setItem(key(), JSON.stringify(s)); } catch (e) {}
  }
  function clearState() {
    try { sessionStorage.removeItem(key()); } catch (e) {}
  }

  function pageOf(href) {
    try { return parseInt(new URL(href, location.origin).searchParams.get('page'), 10) || 1; }
    catch (e) { return 1; }
  }

  function loadNext() {
    var btn = document.querySelector('#results [data-motive-load-more-btn]');
    var box = btn && btn.closest('[data-motive-load-more]');
    var list = document.querySelector('#results .collection-list');
    if (busy || !btn || !box || !list) return Promise.resolve(false);
    busy = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    var href = btn.href;
    var url = new URL(href, location.origin);
    url.searchParams.set('section_id', box.getAttribute('data-section-id'));

    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newList = doc.querySelector('.collection-list');
        if (!newList) throw new Error('no grid in response');
        var newBox = doc.querySelector('[data-motive-load-more]');
        var items = [];
        for (var i = 0; i < newList.children.length; i++) {
          var li = newList.children[i];
          /* skip the page's own scrolling-text / description rows */
          if (li.tagName === 'LI' && !li.classList.contains('col-span-full')) items.push(li);
        }
        var firstNew = null;
        var append = function () {
          /* the theme's last-row placeholders belong at the very end of the grid */
          var ph = list.querySelectorAll(':scope > li[role="presentation"]:not(.motive-density-filler)');
          for (var p = 0; p < ph.length; p++) ph[p].remove();
          for (var j = 0; j < items.length; j++) {
            var node = document.adoptNode(items[j]);
            list.appendChild(node);
            if (!firstNew && node.getAttribute('role') !== 'presentation') firstNew = node;
          }
        };
        if (window.Alpine && Alpine.mutateDom) Alpine.mutateDom(append); else append();
        if (window.Alpine && Alpine.initTree) {
          for (var k = 0; k < items.length; k++) Alpine.initTree(items[k]);
        }
        if (newBox) box.replaceWith(document.adoptNode(newBox)); else box.remove();
        writeState({ pages: pageOf(href), y: window.scrollY });
        /* keyboard users land on the first new product; no scroll jump for anyone */
        var link = firstNew && firstNew.querySelector('a[href]');
        if (link) link.focus({ preventScroll: true });
        return true;
      })
      .catch(function () {
        window.location.href = href;
        return false;
      })
      .then(function (ok) {
        busy = false;
        return ok;
      });
  }

  function restore() {
    var s = readState();
    if (!s || !(s.pages > 1)) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    var step = function () {
      var btn = document.querySelector('#results [data-motive-load-more-btn]');
      if (!btn || pageOf(btn.href) > s.pages) {
        window.scrollTo(0, s.y || 0);
        return;
      }
      loadNext().then(function (ok) { if (ok) step(); });
    };
    step();
  }

  function init() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-motive-load-more-btn]');
      if (!btn || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
      e.preventDefault();
      loadNext();
    });
    window.addEventListener('pagehide', function () {
      var s = readState();
      if (s) { s.y = window.scrollY; writeState(s); }
    });
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type === 'back_forward') restore();
    else clearState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
